import dayjs, {Dayjs} from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';

// dayjs-date-adapter.ts
import {Injectable} from '@angular/core';
import {DateAdapter, MatDateFormats} from '@angular/material/core';

dayjs.extend(localeData);
dayjs.extend(timezone);

@Injectable()
export class DateAdapterService extends DateAdapter<Dayjs> {
	private localeData: any;

	constructor() {
		super();
		dayjs.extend(localeData);
		//set dynamic locale by timezone
		this.setLocale(dayjs.tz.guess());
	}

	addCalendarYears(date: Dayjs, years: number): Dayjs {
		return date.add(years, 'year');
	}

	addCalendarMonths(date: Dayjs, months: number): Dayjs {
		return date.add(months, 'month');
	}

	override setLocale(locale: string) {
		super.setLocale(locale);
		this.localeData = dayjs().localeData();
	}

	getYear(date: Dayjs): number {
		return date.year();
	}

	getMonth(date: Dayjs): number {
		return date.month();
	}

	getDate(date: Dayjs): number {
		return date.date();
	}

	getDayOfWeek(date: Dayjs): number {
		return date.day();
	}

	getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
		return style === 'long' ? this.localeData.months() : this.localeData.monthsShort();
	}

	getDateNames(): string[] {
		return Array.from({length: 31}, (_, i) => String(i + 1));
	}

	getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
		return style === 'long' ? this.localeData.weekdays() : this.localeData.weekdaysShort();
	}

	getYearName(date: Dayjs): string {
		return date.format('YYYY');
	}

	getFirstDayOfWeek(): number {
		return this.localeData.firstDayOfWeek();
	}

	getNumDaysInMonth(date: Dayjs): number {
		return date.daysInMonth();
	}

	clone(date: Dayjs): Dayjs {
		return dayjs(date);
	}

	createDate(year: number, month: number, date: number): Dayjs {
		// Dayjs months are 0-based
		return dayjs(`${year}-${month + 1}-${date}`, 'YYYY-MM-DD');
	}

	today(): Dayjs {
		return dayjs();
	}

	parse(value: any, parseFormat: string | string[]): Dayjs | null {
		if (!value) return null; // Immediately return null for falsy values

		if (typeof value === 'string') {
			const parsedDate = dayjs(value, parseFormat, true); // Use strict parsing
			return parsedDate.isValid() ? parsedDate : null;
		}

		return dayjs.isDayjs(value) ? value : dayjs(value); // Directly return if already a Dayjs object, or try to parse otherwise
	}

	format(date: Dayjs, displayFormat: string): string {
		return date.format(displayFormat);
	}

	addCalendarDays(date: Dayjs, days: number): Dayjs {
		return date.add(days, 'day');
	}

	toIso8601(date: Dayjs): string {
		return date.toISOString();
	}

	fromIso8601(isoString: string): Dayjs {
		return dayjs(isoString);
	}

	isDateInstance(obj: any): boolean {
		return dayjs.isDayjs(obj);
	}

	isValid(date: Dayjs): boolean {
		return date.isValid();
	}

	invalid(): Dayjs {
		// Create a dayjs object with an invalid date
		return dayjs('Invalid Date');
	}
}

export const DAYJS_DATE_FORMATS: MatDateFormats = {
	parse: {
		dateInput: 'DD/MM/YYYY',
	},
	display: {
		dateInput: 'DD/MM/YYYY',
		monthYearLabel: 'MMM YYYY',
		dateA11yLabel: 'LL',
		monthYearA11yLabel: 'MMMM YYYY',
	},
};
